import React from 'react'
import { motion } from 'framer-motion'
import {
  Loader2,
  ClipboardList,
  Sparkles,
  Tv,
  Users,
  LayoutTemplate,
  Globe,
  type LucideIcon,
} from 'lucide-react'
import { CampaignData } from '../../types'
import { MediaPlanner } from '../campaignproposal/mediaplanner'
import { AudiencePlanner } from '../campaignproposal/audienceplanner'
import { TemplatesRecommender } from '../campaignproposal/templatesrecommender'
import { SiteListPlanner } from '../campaignproposal/sitelistplanner'

interface CampaignProposalTabProps {
  data: CampaignData | null
  activeSubTab: string
  onSubTabChange: (tab: string) => void
  onCreateAdUnits?: (selectedTemplateIds: string[], selectedSizes: Map<string, Set<string>>) => void
  isOrchestratorRunning?: boolean
}

interface SubTab {
  id: string
  label: string
  icon: LucideIcon
}

const allSubTabs: SubTab[] = [
  { id: 'media', label: 'Media Planner', icon: Tv },
  { id: 'audience', label: 'Audience Planner', icon: Users },
  { id: 'templates', label: 'Templates Recommender', icon: LayoutTemplate },
  { id: 'sitelist', label: 'Site List Planner', icon: Globe },
]

function SubTabSkeleton({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Generating {label}…
      </p>
    </div>
  )
}

function hasMediaPlanContent(data: CampaignData | null): boolean {
  if (!data?.mediaPlan) return false
  const mp = data.mediaPlan
  return !!(
    (mp.channels && mp.channels.length > 0) ||
    (mp.placements && mp.placements.length > 0) ||
    (mp.budget_allocation && mp.budget_allocation.total_cost > 0) ||
    mp.campaign_name
  )
}

function hasTemplatesContent(data: CampaignData | null): boolean {
  if (!data) return false
  return !!(
    (data.highImpactTemplates && data.highImpactTemplates.length > 0) ||
    (data.programmaticTemplates && data.programmaticTemplates.length > 0) ||
    (data.doohTemplates && data.doohTemplates.length > 0) ||
    (data.templates && data.templates.length > 0)
  )
}

function hasSiteListContent(data: CampaignData | null): boolean {
  if (!data) return false
  return !!(
    (data.mediaPlan?.sitelists && data.mediaPlan.sitelists.length > 0) ||
    (data.audiencePlan?.siteList?.sites && data.audiencePlan.siteList.sites.length > 0)
  )
}

function hasAudienceContent(data: CampaignData | null): boolean {
  if (!data?.audiencePlan) return false
  return !!(data.audiencePlan.segments && data.audiencePlan.segments.length > 0)
}

export function CampaignProposalTab({ data, activeSubTab, onSubTabChange, onCreateAdUnits, isOrchestratorRunning = false }: CampaignProposalTabProps) {
  const hasMedia = hasMediaPlanContent(data)
  const hasTemplates = hasTemplatesContent(data)
  const hasSiteList = hasSiteListContent(data)
  const hasAudience = hasAudienceContent(data)

  // Top-level empty state for campaigns without any proposal data (and no
  // active orchestrator run). Invites the user to drive generation via chat.
  if (!data && !isOrchestratorRunning) {
    return (
      <div className="h-full flex items-center justify-center p-6 lg:p-10">
        <div className="text-center max-w-md">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-primary-500/10 to-blue-500/10 mb-5">
            <ClipboardList className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No proposal yet
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Use the AI Assistant on the left to generate a campaign proposal —
            media plan, audience targeting, templates and curated site lists
            will appear here.
          </p>
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-900/40 text-primary-700 dark:text-primary-300 text-xs font-medium">
            <Sparkles className="h-3.5 w-3.5" />
            Try “Start a campaign proposal” in the chat
          </div>
        </div>
      </div>
    )
  }

  // When orchestrator is running, show all core sub-tabs; otherwise only those with data
  const subTabs = allSubTabs.filter(tab => {
    switch (tab.id) {
      case 'media':
        return hasMedia || isOrchestratorRunning
      case 'audience':
        return hasAudience
      case 'templates':
        return hasTemplates || isOrchestratorRunning
      case 'sitelist':
        return hasSiteList || isOrchestratorRunning
      default:
        return false
    }
  })

  const visibleSubTabs = subTabs.length > 0 ? subTabs : [allSubTabs[0]]

  const validActiveTab = visibleSubTabs.some(t => t.id === activeSubTab)
    ? activeSubTab
    : visibleSubTabs[0]?.id || 'media'

  // Per-tab loading state: data not yet arrived but orchestrator is still working
  const isTabLoading = (tabId: string): boolean => {
    if (!isOrchestratorRunning) return false
    switch (tabId) {
      case 'media': return !hasMedia
      case 'templates': return !hasTemplates
      case 'sitelist': return !hasSiteList
      default: return false
    }
  }

  const renderSubTabContent = () => {
    if (isTabLoading(validActiveTab)) {
      const tab = allSubTabs.find(t => t.id === validActiveTab)
      return <SubTabSkeleton label={tab?.label || 'data'} />
    }

    switch (validActiveTab) {
      case 'media':
        return <MediaPlanner data={data!.mediaPlan} />
      case 'audience':
        return <AudiencePlanner data={data!.audiencePlan} />
      case 'templates':
        return (
          <TemplatesRecommender
            highImpactTemplates={data?.highImpactTemplates || []}
            programmaticTemplates={data?.programmaticTemplates || []}
            doohTemplates={data?.doohTemplates || []}
            onCreateAdUnits={onCreateAdUnits}
          />
        )
      case 'sitelist': {
        const siteListData = data?.mediaPlan?.sitelists && data.mediaPlan.sitelists.length > 0
          ? data.mediaPlan.sitelists
          : data?.audiencePlan?.siteList || null

        return siteListData ? (
          <SiteListPlanner data={siteListData} />
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-gray-500">Site list data not available yet.</p>
            </div>
          </div>
        )
      }
      default:
        return <MediaPlanner data={data!.mediaPlan} />
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Sticky Sub-tab Navigation */}
      <div className="sticky top-0 z-20 border-b border-gray-200/30 dark:border-slate-800/50 bg-gray-50 dark:bg-slate-900 flex-shrink-0">
        <nav className="flex items-center px-3 py-2 overflow-x-auto">
          {visibleSubTabs.map((tab, index) => {
            const Icon = tab.icon
            return (
            <React.Fragment key={tab.id}>
              <button
                onClick={() => onSubTabChange(tab.id)}
                className={`relative flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors duration-200 whitespace-nowrap ${
                  validActiveTab === tab.id
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200/70 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-slate-700/60'
                }`}
              >
                <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="hidden md:inline">{tab.label}</span>
                <span className="md:hidden">{tab.label.split(' ')[0]}</span>
                {isTabLoading(tab.id) && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                  </span>
                )}
              </button>
              {index < visibleSubTabs.length - 1 && (
                <div className="h-5 w-px bg-gray-300 dark:bg-slate-700 mx-1.5 flex-shrink-0"></div>
              )}
            </React.Fragment>
            )
          })}
        </nav>
      </div>

      {/* Scrollable Sub-tab Content */}
      <div className="flex-1 overflow-y-auto min-h-0 p-4 lg:p-6 scrollbar-thin">
        <motion.div
          key={validActiveTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {renderSubTabContent()}
        </motion.div>
      </div>
    </div>
  )
}

